// Reaktiver i18n-Store (PFS-708 / Epic PFS-418) — der Framework-Seam für
// Übersetzungen. EIN Mechanismus, überall gleich: generische pfs-*-WCs UND
// Cut-App-Domänen-WCs resolven dieselben Keys gegen DENSELBEN aktiven Katalog.
//
// **Global-backed** (`globalThis`, wie context-store): in der Cut-App+Vendor-
// Shell-Architektur bundlet jede App ihre eigene @pfs/components-Kopie — ein
// Modul-Level-State wäre pro Bundle getrennt. Das Global teilen alle Instanzen.
//
// Grenze (domänen-opak): der Store hält nur `messages` (key→text) + `locale`.
// Die *Bedeutung* (tenant-Wording) liefert der Host über den Loader
// (`<pfs-i18n-catalog>` → setCatalog). Fallback-Kette: Katalog → Default → Key.

export const PFS_I18N_CHANGED_EVENT = "pfs:i18n-changed";
export const PFS_I18N_LOCALE_REQUEST_EVENT = "pfs:i18n-locale-request";

interface I18nState {
  locale: string;
  messages: Record<string, string>;
  ready: boolean;
}

declare global {
  // Geteilter i18n-Store über ALLE @pfs/components-Bundle-Kopien.
  // eslint-disable-next-line no-var
  var __pfsI18nStore: I18nState | undefined;
}

function state(): I18nState {
  return (globalThis.__pfsI18nStore ??= { locale: "", messages: {}, ready: false });
}

// translate: DER Übersetzungs-Seam. Fallback-Kette Katalog[key] → default → key.
// Ein WC ruft `translate("scheduling.appointments.title", "Appointments")` — der
// Default ist der self-contained (englische) Komponenten-Text; der Katalog des
// aktiven Tenants/Locale überschreibt ihn; fehlt beides, ist der Key sichtbar
// (nie „undefined"). NIE ein rohes tenant-Wording im generischen WC.
export function translate(key: string, def?: string): string {
  const msg = state().messages;
  const hit = msg[key];
  if (hit !== undefined && hit !== "") return hit;
  return def ?? key;
}

// Kurz-Alias.
export const t = translate;

// getLocale: die aktive Locale (leer = noch kein Katalog geladen).
export function getLocale(): string {
  return state().locale;
}

// isI18nReady: true sobald ein Katalog gesetzt wurde.
export function isI18nReady(): boolean {
  return state().ready;
}

// setCatalog: vom Loader gerufen, sobald der Katalog (für die aktive Locale)
// geladen ist. Setzt messages + locale + ready und broadcastet PFS_I18N_CHANGED
// → alle withI18n-Konsumenten re-rendern.
export function setCatalog(locale: string, messages: Record<string, string>): void {
  const s = state();
  s.locale = locale;
  s.messages = messages ?? {};
  s.ready = true;
  broadcast();
}

// requestLocale: vom `<pfs-locale-toggle>` gerufen — der Wunsch, die aktive
// Locale live zu wechseln. Broadcastet PFS_I18N_LOCALE_REQUEST; der Loader hört,
// re-fetcht den Katalog für die neue Locale und ruft setCatalog (→ Re-Render).
// pfs-components setzt hier KEINEN Cookie/keine Persistenz — das gehört dem Host
// (wie beim Theme). Setzt aber `<html lang>` (nativer Vertrag), damit der Loader
// + native UA-Formatierung die Locale sehen.
export function requestLocale(locale: string): void {
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
    document.body?.dispatchEvent(
      new CustomEvent(PFS_I18N_LOCALE_REQUEST_EVENT, { detail: { locale } }),
    );
  }
}

function broadcast(): void {
  if (typeof document !== "undefined") {
    document.body?.dispatchEvent(new CustomEvent(PFS_I18N_CHANGED_EVENT));
  }
}
